<template>
  <BaseLayout>
    <div>
      <div class="header-bar">
        <!--
          One navbar per page, never two. The home route renders the lean
          `TenantNavbar`, which reads the brand name from the public
          `GET /api/v1/current_tenant` endpoint; every other route keeps `MainMenu`,
          which carries the navigation links, the language switcher and the mobile
          drawer that the home page has no room for.
        -->
        <TenantNavbar v-if="isHome" />
        <MainMenu v-else />
      </div>

      <section v-if="showTenantBookingWidget" class="tenant-booking-rail">
        <AppointmentBookingWidget />
      </section>

      <div id="main-content" class="container mt-0" tabindex="-1">
        <router-view />
      </div>

      <SiteFooter v-if="!isHome" />

      <ServiceSeoDirector />
    </div>
  </BaseLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import BaseLayout from '../BaseLayout.vue';
import MainMenu from './MainMenu.vue';
import TenantNavbar from '@/components/website/TenantNavbar.vue';
import SiteFooter from '@/components/website/SiteFooter.vue';
import ServiceSeoDirector from '@/components/seo/ServiceSeoDirector.vue';
import AppointmentBookingWidget from '@/components/scheduling/AppointmentBookingWidget.vue';
import { loadBranding } from '@/services/branding.service';

const route = useRoute();

const isHome = computed(() => route.name === 'home');

// The star scheduling widget is the tenant's primary call to action, so it sits
// directly under the navbar on every tenant page, the home page included.
//
// The apex does not get it: the platform is a booking directory, and a tenant
// widget on it would offer a single clinic's agenda as if it were the platform's
// own.
//
// It used to be suppressed on the home route as well, because HomePage rendered
// its own copy and two of them would stack on the same screen. HomePage no longer
// renders it -- on the apex that page holds the brand directory, on a brand host it
// renders nothing -- so the exclusion had no reason left to exist and keeping it
// would have dropped the agenda off `/` entirely.
const isTenant = computed(() => !loadBranding().isPlatform);
const showTenantBookingWidget = computed(() => isTenant.value);
</script>

<style scoped>
/* Same treatment as the home page section so the widget reads as one component
   whether it was reached from `/` or from any other tenant page. */
.tenant-booking-rail {
  background: linear-gradient(180deg, #f7f5fb 0%, #efe9f7 100%);
  padding: clamp(1.5rem, 3.5vw, 2.5rem) clamp(1rem, 5vw, 2rem);
  display: flex;
  justify-content: center;
}

@media (max-width: 720px) {
  .tenant-booking-rail {
    padding: 1.25rem 0.75rem;
  }
}
</style>
